import { lazy, Suspense, useRef, useEffect, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform, useMotionValue, animate } from "framer-motion";
import { BadgeCheck, Zap, Handshake, ArrowRight, ArrowUpRight, Check, Linkedin, ExternalLink, ChevronDown, Search, FileText, Coins, TrendingUp } from "lucide-react";
import { useTranslation, useLang } from "@/lib/i18n";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Navbar from "@/components/Navbar";
import BackToTop from "@/components/BackToTop";
import { CountUp } from "@/components/CountUp";
import { AvailabilityPill } from "@/components/AvailabilityPill";
import SiteFooter from "@/components/SiteFooter";

const NeuralNetwork3D = lazy(() => import("@/components/NeuralNetwork3D"));

const cardIcons = [BadgeCheck, Zap, Handshake];

/* ── Inline count-up metric ─────────────────────────────────────────────── */
function AnimatedMetric({ value, suffix, label, delay = 0 }: { value: string; suffix: string; label: string; delay?: number }) {
  const num = parseInt(value, 10);
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(0);
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    if (prefersReduced) { setDisplay(num); return; }
    const timeout = setTimeout(() => {
      const ctrl = animate(mv, num, { duration: 1.2, ease: [0.16, 1, 0.3, 1] });
      const unsub = mv.on("change", (v) => setDisplay(Math.round(v)));
      return () => { ctrl.stop(); unsub(); };
    }, delay * 1000);
    return () => clearTimeout(timeout);
  }, [num, delay, mv, prefersReduced]);

  return (
    <div className="flex flex-col gap-1 py-4 lg:py-0 lg:px-5">
      <span className="sr-only">{value}{suffix} {label}</span>
      <span role="img" aria-hidden="true" className="text-5xl lg:text-6xl font-display tabular-nums text-foreground leading-none whitespace-nowrap">
        {display}<span className="text-primary">{suffix}</span>
      </span>
      <span className="text-xs font-mono font-semibold tracking-[0.15em] text-muted-foreground uppercase mt-1">{label}</span>
    </div>
  );
}

const Gateway = () => {
  const t = useTranslation();
  const { lang } = useLang();
  const g = t.gateway;
  const prefersReduced = useReducedMotion();

  useDocumentMeta({
    title: g.meta.title,
    description: g.meta.description,
    canonical: lang === "pl" ? "https://chrisworkflows.com/pl" : "https://chrisworkflows.com/",
    robots: "index, follow",
    ogTitle: g.meta.title,
    ogDescription: g.meta.description,
    ogImage: `https://chrisworkflows.com/og/gateway-${lang}.jpg?v=3`,
  });

  const anim = prefersReduced
    ? {
        initial: {},
        whileInView: {},
        viewport: { once: true } as const,
        transition: { duration: 0 },
      }
    : {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-80px" } as const,
        transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
      };

  /* ── Cursor spotlight ref ────────────────────────────────────────────── */
  const heroRef = useRef<HTMLElement>(null);
  const spotlightRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const hero = heroRef.current;
    const spot = spotlightRef.current;
    if (!hero || !spot || prefersReduced) return;
    if (!window.matchMedia("(pointer: fine)").matches) return;

    const onMove = (e: MouseEvent) => {
      const rect = hero.getBoundingClientRect();
      spot.style.setProperty("--mx", `${e.clientX - rect.left}px`);
      spot.style.setProperty("--my", `${e.clientY - rect.top}px`);

      // Magnetic CTA — section-level listener so button responds from far away
      if (ctaRef.current) {
        const r = ctaRef.current.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dx = (e.clientX - cx) * 0.08;
        const dy = (e.clientY - cy) * 0.08;
        const max = 8;
        ctaRef.current.style.transform = `translate(${Math.max(-max, Math.min(max, dx))}px, ${Math.max(-max, Math.min(max, dy))}px)`;
      }
    };
    const onLeave = () => {
      const rect = hero.getBoundingClientRect();
      spot.style.setProperty("--mx", `${rect.width / 2}px`);
      spot.style.setProperty("--my", `${rect.height / 2}px`);
      if (ctaRef.current) ctaRef.current.style.transform = "translate(0, 0)";
    };

    hero.addEventListener("mousemove", onMove);
    hero.addEventListener("mouseleave", onLeave);
    return () => { hero.removeEventListener("mousemove", onMove); hero.removeEventListener("mouseleave", onLeave); };
  }, [prefersReduced]);

  /* ── Defer 3D background mount so hero paints first (skip on mobile) ── */
  const [showBackground, setShowBackground] = useState(false);
  useEffect(() => {
    if (window.innerWidth < 1024) return; // skip Three.js on mobile/tablet
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    if (ric) {
      const id = ric(() => setShowBackground(true), { timeout: 1500 });
      return () => (window as unknown as { cancelIdleCallback: (id: number) => void }).cancelIdleCallback(id);
    }
    const id = setTimeout(() => setShowBackground(true), 600);
    return () => clearTimeout(id);
  }, []);

  /* ── Mobile detection for animation fallbacks (opacity-only on mobile) ── */
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1024 : false,
  );
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* ── Scroll affordance fade ──────────────────────────────────────────── */
  const { scrollY } = useScroll();
  const scrollOpacity = useTransform(scrollY, [0, 60], [1, 0]);

  /* ── Word-split helper ───────────────────────────────────────────────── */
  const wordStagger = 0.06;
  const wordEase: [number, number, number, number] = [0.16, 1, 0.3, 1];

  const renderWords = (text: string, startIndex: number, className?: string) =>
    text.split(" ").map((word, i) => (
      <motion.span
        key={startIndex + i}
        initial={prefersReduced || isMobile ? false : { opacity: 0, y: 20, filter: "blur(8px)" }}
        animate={prefersReduced || isMobile ? undefined : { opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{ delay: 0.3 + (startIndex + i) * wordStagger, duration: 0.7, ease: wordEase }}
        className={`inline-block mr-[0.1em] pr-[0.08em] ${className || ""}`}
      >
        {word}
      </motion.span>
    ));

  const line1Words = g.hero.headingLine1.split(" ").length;

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-x-hidden">
      {showBackground && (
        <Suspense fallback={null}>
          <NeuralNetwork3D />
        </Suspense>
      )}

      <div className="relative z-10">
        <Navbar />

        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <section ref={heroRef} className="relative flex flex-col min-h-[65vh] pt-10">
          {/* Cursor spotlight */}
          <div
            ref={spotlightRef}
            className="absolute inset-0 pointer-events-none z-0"
            style={{
              background: "radial-gradient(600px circle at var(--mx, 50%) var(--my, 50%), hsl(var(--primary) / 0.06), transparent 100%)",
              mixBlendMode: "screen",
            }}
          />

          {/* Subtle grid */}
          <div
            className="absolute inset-0 opacity-[0.025]"
            style={{
              backgroundImage:
                "linear-gradient(hsl(166 84% 46%) 1px, transparent 1px), linear-gradient(90deg, hsl(166 84% 46%) 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />
          {/* Glow */}
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-primary/5 blur-[120px]" />

          <div className="flex-1 flex items-center">
            <div className="container mx-auto px-6 xl:max-w-[1400px] 2xl:max-w-[1600px] relative z-10 py-12">
              {/* Availability pill */}
              <div className="flex justify-end mb-6 lg:mb-0 lg:absolute lg:top-20 lg:right-6">
                <AvailabilityPill text={g.hero.availability} />
              </div>

              <div className="flex flex-col lg:flex-row lg:items-center lg:gap-16">
                {/* Left column: text */}
                <div className="lg:w-[65%] 2xl:w-[75%]">
                  <motion.p
                    initial={isMobile ? false : { opacity: 0, y: 10 }}
                    animate={isMobile ? undefined : { opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="flex items-center gap-3 text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-8"
                  >
                    <span className="w-8 h-px bg-primary animate-pulse-glow" />
                    {g.hero.eyebrow}
                  </motion.p>

                  <h1 className="text-4xl sm:text-6xl lg:text-7xl 2xl:text-8xl font-display leading-[1.05] tracking-tight mb-8">
                    <span className="block">{renderWords(g.hero.headingLine1, 0)}</span>
                    <span className="block">{renderWords(g.hero.headingHighlight, line1Words, "text-gradient")}</span>
                    <span className="block">{renderWords(g.hero.headingHighlight2, line1Words + g.hero.headingHighlight.split(" ").length, "text-gradient")}</span>
                  </h1>

                  <motion.p
                    initial={isMobile ? false : { opacity: 0, y: 16 }}
                    animate={isMobile ? undefined : { opacity: 1, y: 0 }}
                    transition={{ delay: 0.45, duration: 0.5 }}
                    className="text-lg font-body text-muted-foreground leading-relaxed max-w-xl"
                  >
                    {g.hero.sub}
                  </motion.p>

                  {/* Hero CTAs */}
                  <motion.div
                    initial={isMobile ? false : { opacity: 0, y: 12 }}
                    animate={isMobile ? undefined : { opacity: 1, y: 0 }}
                    transition={{ delay: 0.55, duration: 0.5 }}
                    className="mt-8 flex items-center gap-5"
                  >
                    <div>
                      <a
                        ref={ctaRef}
                        href="#services"
                        className="inline-flex items-center gap-2 px-7 py-3.5 min-h-[44px] rounded-full bg-primary text-primary-foreground font-body font-semibold text-sm hover:bg-primary/90 transition-all glow"
                        style={{ transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms" }}
                      >
                        {g.hero.cta}
                        <ArrowRight size={16} />
                      </a>
                    </div>
                  </motion.div>
                </div>

                {/* Right column: typographic metrics */}
                <div className="lg:w-[35%] mt-12 lg:mt-0 relative">
                  {/* Dead-zone filler: rotating conic gradient */}
                  <div
                    className="absolute inset-0 -m-8 pointer-events-none hidden lg:block opacity-[0.06]"
                    style={{
                      background: "conic-gradient(from 0deg, hsl(var(--primary)), transparent, hsl(var(--accent)))",
                      animation: "spin 30s linear infinite",
                      borderRadius: "50%",
                      filter: "blur(80px)",
                    }}
                  />

                  <div className="flex flex-col divide-y divide-border/50 lg:flex-row lg:divide-y-0 lg:divide-x lg:divide-border/50 relative z-10">
                    {g.hero.statsPills.map((pill, i) => (
                      <AnimatedMetric
                        key={i}
                        value={pill.value}
                        suffix={pill.suffix}
                        label={pill.label}
                        delay={0.7 + i * 0.15}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll affordance */}
          <motion.div
            style={{ opacity: scrollOpacity }}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 pointer-events-none z-10"
          >
            <span className="text-[11px] font-mono text-muted-foreground tracking-widest">{t.scrollLabel}</span>
            <ChevronDown size={14} className="text-muted-foreground animate-bounce" />
          </motion.div>
        </section>

        {/* ── Service Cards ─────────────────────────────────────────────────── */}
        <section id="services" className="py-8 pb-16">
          <div className="container mx-auto px-6">
            <div className="grid gap-6 lg:grid-cols-12 lg:grid-rows-2">
              {g.cards.map((card, i) => {
                const isFirst = i === 0;
                const isAdvisory = i === 2;
                const accentColor = isAdvisory ? "text-accent" : "text-primary";

                const handleMouseMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
                  if (!window.matchMedia("(pointer: fine)").matches) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                  const y = ((e.clientY - rect.top) / rect.height) * 100;
                  e.currentTarget.style.setProperty("--card-mx", `${x}%`);
                  e.currentTarget.style.setProperty("--card-my", `${y}%`);
                };

                const handleMouseLeave = (e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.currentTarget.style.setProperty("--card-mx", "50%");
                  e.currentTarget.style.setProperty("--card-my", "50%");
                };

                return (
                  <motion.a
                    key={i}
                    href={card.href}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    initial={prefersReduced || isMobile ? false : { opacity: 0, y: 20 }}
                    whileInView={prefersReduced || isMobile ? undefined : { opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ delay: i * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className={`group relative rounded-2xl border p-7 flex flex-col transition-all duration-300 overflow-hidden ${
                      isFirst
                        ? "lg:col-span-6 lg:row-span-2 lg:justify-start lg:pt-8 lg:pb-8 border-primary/40 bg-primary/5 hover:bg-primary/10 hover:border-primary/50"
                        : "lg:col-span-6 lg:row-span-1 border-border/60 bg-card/60 hover:border-primary/40 hover:ring-1 hover:ring-primary/10"
                    }`}
                  >
                    {/* Glow spotlight overlay */}
                    <div
                      className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{
                        background: `radial-gradient(circle at var(--card-mx, 50%) var(--card-my, 50%), hsl(var(--primary) / ${isFirst ? "0.20" : "0.15"}), transparent 60%)`,
                      }}
                    />

                    {/* Flagship conic gradient */}
                    {isFirst && (
                      <div
                        className="pointer-events-none absolute bottom-0 right-0 w-[40%] h-[40%] opacity-[0.06]"
                        style={{
                          background: "conic-gradient(from 0deg, hsl(var(--primary)), hsl(var(--accent)), transparent)",
                          animation: "spin 40s linear infinite",
                          filter: "blur(60px)",
                        }}
                      />
                    )}

                    {/* Badge + eyebrow + corner arrow */}
                    <div className="relative z-10 flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {!isFirst && card.badge && (
                          <span className="text-[10px] font-body font-bold tracking-widest px-2.5 py-1 rounded-full bg-border/60 text-muted-foreground">
                            {card.badge}
                          </span>
                        )}
                        <span className="text-[10px] font-mono font-semibold tracking-[0.2em] text-primary uppercase">
                          {card.eyebrow}
                        </span>
                      </div>
                      <ArrowUpRight
                        size={22}
                        className={`text-muted-foreground ${isAdvisory ? "group-hover:text-accent" : "group-hover:text-primary"} group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300`}
                        strokeWidth={1.5}
                      />
                    </div>

                    {/* Title + desc */}
                    <h2 className="relative z-10 text-3xl font-display text-foreground mb-3">{card.title}</h2>
                    <p className="relative z-10 text-base font-body text-muted-foreground leading-relaxed mb-4">
                      {card.desc}
                    </p>

                    {/* Flagship deliverable tiles */}
                    {isFirst && (() => {
                      const iconMap = { Search, FileText, Coins, TrendingUp } as Record<string, typeof Search>;
                      return (
                        <div className="relative z-10 mt-4 mb-5">
                          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-primary/60 mb-2 block">{lang === "pl" ? "ZAKRES" : "DELIVERABLES"}</span>
                          <div className="grid grid-cols-2 gap-2">
                            {g.flagship.deliverables.map((d: { icon: string; label: string }, j: number) => {
                              const Icon = iconMap[d.icon];
                              return (
                                <motion.div
                                  key={j}
                                  initial={prefersReduced || isMobile ? false : { opacity: 0, y: 6 }}
                                  whileInView={prefersReduced || isMobile ? undefined : { opacity: 1, y: 0 }}
                                  viewport={{ once: true }}
                                  transition={{ delay: j * 0.06, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                  className="rounded-lg border border-border/40 bg-background/60 p-3 flex flex-col items-center text-center gap-1.5 hover:border-primary/30 transition-colors duration-200"
                                >
                                  {Icon && <Icon size={16} className="text-primary/70" />}
                                  <span className="font-mono text-xs uppercase tracking-[0.12em] text-muted-foreground">{d.label}</span>
                                </motion.div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Item list */}
                    <ul className="relative z-10 space-y-2 mb-6">
                      {card.items.map((item, j) => (
                        <motion.li
                          key={j}
                          className="flex items-center gap-2.5"
                          initial={prefersReduced ? undefined : { opacity: 0 }}
                          whileInView={prefersReduced ? undefined : { opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: j * 0.04, duration: 0.4 }}
                        >
                          <span className="text-primary text-sm flex-shrink-0">{"\u2014"}</span>
                          <span className="text-sm font-body text-muted-foreground">{item}</span>
                        </motion.li>
                      ))}
                    </ul>

                    {/* Inline CTA text */}
                    <span className={`relative z-10 text-sm font-body font-semibold ${accentColor}`}>
                      {card.cta}
                    </span>
                  </motion.a>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── About strip ───────────────────────────────────────────────────── */}
        <section className="pt-6 pb-12 border-y border-border/40 bg-card/20 relative overflow-hidden">
          {/* Editorial separator */}
          <div className="container mx-auto px-6 mb-10">
            <div className="flex items-center gap-3 max-w-5xl mx-auto">
              <div className="flex-1 h-px bg-border/30" />
              <span className="font-mono text-[8px] sm:text-[10px] uppercase tracking-[0.15em] text-muted-foreground px-3 whitespace-nowrap">
                {lang === "pl" ? "10 LAT DOŚWIADCZENIA · POLSKA · DOSTĘPNY ZDALNIE" : "CREDIBILITY · 10 YEARS · POLAND · REMOTE"}
              </span>
              <div className="flex-1 h-px bg-border/30" />
            </div>
          </div>
          {/* Diagonal pinstripe background */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: "repeating-linear-gradient(135deg, hsl(var(--primary) / 0.03) 0 1px, transparent 1px 48px)",
            }}
          />

          <div className="container mx-auto px-6 relative z-10">
            <div className="grid lg:grid-cols-12 items-center gap-10 max-w-5xl mx-auto">
              {/* Text — left column */}
              <div className="lg:col-span-7">
                <motion.p
                  initial={prefersReduced || isMobile ? false : { opacity: 0, y: 20 }}
                  whileInView={prefersReduced || isMobile ? undefined : { opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-4"
                >
                  {g.about.eyebrow}
                </motion.p>
                <motion.h2
                  initial={prefersReduced || isMobile ? false : { opacity: 0, y: 20 }}
                  whileInView={prefersReduced || isMobile ? undefined : { opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
                  className="text-3xl sm:text-4xl font-display tracking-tight mb-5"
                >
                  {g.about.heading}
                </motion.h2>
                <motion.p
                  initial={prefersReduced || isMobile ? false : { opacity: 0, y: 20 }}
                  whileInView={prefersReduced || isMobile ? undefined : { opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
                  className="text-lg font-display text-muted-foreground leading-relaxed mb-5"
                >
                  {g.about.body}
                </motion.p>
                <motion.a
                  initial={prefersReduced || isMobile ? false : { opacity: 0, y: 20 }}
                  whileInView={prefersReduced || isMobile ? undefined : { opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
                  href={g.about.linkedinHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 text-sm font-body font-semibold text-primary mt-6 min-h-[44px] relative"
                >
                  <Linkedin size={14} />
                  <span className="relative">
                    {g.about.linkedinLabel}
                    <span className="absolute bottom-0 left-0 w-0 h-px bg-primary transition-all duration-150 group-hover:w-full" />
                  </span>
                  <ExternalLink size={14} />
                </motion.a>
              </div>

              {/* Stats — right column: typographic 2×2 grid */}
              <div className="lg:col-span-5">
                <div className="grid grid-cols-2">
                  {g.stats.map((stat, i) => (
                    <motion.div
                      key={i}
                      initial={prefersReduced || isMobile ? false : { opacity: 0, y: 16 }}
                      whileInView={prefersReduced || isMobile ? undefined : { opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ delay: 0.08 * i, duration: 0.5 }}
                      className={[
                        "p-5",
                        // Right border on left-column cells (index 0, 2)
                        i % 2 === 0 ? "border-r border-border/40" : "",
                        // Bottom border on top-row cells (index 0, 1)
                        i < 2 ? "border-b border-border/40" : "",
                      ].join(" ")}
                    >
                      <p className="font-display text-5xl tabular-nums leading-none mb-1">
                        <CountUp value={stat.value} />
                        <span className="text-2xl text-primary">{stat.suffix}</span>
                      </p>
                      <p className="text-xs font-body uppercase tracking-wider text-muted-foreground mt-2">
                        {stat.label}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Footer CTA ──────────────────────────────────────────────────── */}
        <section className="pt-20 md:pt-24 pb-6 bg-background border-t border-border/30 relative overflow-hidden">
          {/* Ambient glow */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-primary/25 blur-[120px] pointer-events-none"
            style={{ mixBlendMode: "screen" }}
          />

          <div className="container mx-auto px-6 max-w-4xl text-center relative z-10">
            {/* Headline with word-split reveal */}
            <h2 className="text-6xl md:text-8xl lg:text-9xl font-display leading-[1.05] tracking-tight mb-14">
              {g.footer.heading1.split(" ").map((word, i) => (
                <motion.span
                  key={`fh1-${i}`}
                  initial={prefersReduced || isMobile ? false : { opacity: 0, y: 20, filter: "blur(8px)" }}
                  whileInView={prefersReduced || isMobile ? undefined : { opacity: 1, y: 0, filter: "blur(0px)" }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="inline-block mr-[0.1em] pr-[0.08em]"
                >
                  {word}
                </motion.span>
              ))}
              <br />
              {g.footer.headingHighlight.split(" ").map((word, i) => (
                <motion.span
                  key={`fh2-${i}`}
                  initial={prefersReduced || isMobile ? false : { opacity: 0, y: 20, filter: "blur(8px)" }}
                  whileInView={prefersReduced || isMobile ? undefined : { opacity: 1, y: 0, filter: "blur(0px)" }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: (g.footer.heading1.split(" ").length + i) * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="inline-block mr-[0.1em] pr-[0.08em] text-gradient"
                >
                  {word}
                </motion.span>
              ))}
            </h2>

            {/* Magnetic CTA */}
            <motion.div
              initial={prefersReduced || isMobile ? false : { opacity: 0, y: 30 }}
              whileInView={prefersReduced || isMobile ? undefined : { opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: 0.4, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="inline-block"
              onMouseMove={(e: React.MouseEvent) => {
                if (prefersReduced || !window.matchMedia("(pointer: fine)").matches) return;
                const target = e.currentTarget.firstElementChild as HTMLElement;
                if (!target) return;
                const rect = target.getBoundingClientRect();
                const cx = rect.left + rect.width / 2;
                const cy = rect.top + rect.height / 2;
                const dx = Math.max(-10, Math.min(10, (e.clientX - cx) * 0.15));
                const dy = Math.max(-10, Math.min(10, (e.clientY - cy) * 0.15));
                target.style.transform = `translate(${dx}px, ${dy}px)`;
              }}
              onMouseLeave={(e: React.MouseEvent) => {
                const target = e.currentTarget.firstElementChild as HTMLElement;
                if (target) target.style.transform = "translate(0, 0)";
              }}
            >
              <a
                href={lang === "pl" ? "/pl/ai-teams#book" : "/ai-teams#book"}
                className="inline-flex items-center gap-3 px-12 py-6 rounded-full bg-primary text-primary-foreground font-body font-semibold text-base hover:bg-primary/90 transition-all glow"
                style={{ transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms" }}
              >
                {g.footer.cta}
                <ArrowRight size={18} />
              </a>
            </motion.div>

          </div>

          <div className="container mx-auto px-6">
            <SiteFooter />
          </div>
        </section>
      </div>

      <BackToTop />
    </div>
  );
};

export default Gateway;
